import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { API_BASE_URL } from '../../../config/apiConfig';

/**
 * ProductStatusChangeReport - Printable report component for product status changes
 * Displays all product status change information in a print-friendly format
 * Based on the MERCK PRODUCT STATUS CHANGE CONFIRMATION form design
 */
const ProductStatusChangeReport = ({ productStatusChange }) => {
  const [companyLogo, setCompanyLogo] = useState(null);
  const [logoFetchAttempted, setLogoFetchAttempted] = useState(false);

  // Fetch company logo from application settings
  useEffect(() => {
    if (logoFetchAttempted) return;
    
    const fetchCompanyLogo = async () => {
      setLogoFetchAttempted(true);
      
      try {
        const token = localStorage.getItem('authToken');
        const url = `${API_BASE_URL}/application-setting/key/companyLogo`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        });

        if (response.ok) {
          const setting = await response.json();
          
          if (setting && setting.value && setting.isActive !== false) {
            let logoUrl = setting.value.trim();
            
            if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
              const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
              
              if (logoUrl.startsWith('/')) {
                logoUrl = `${baseUrl}${logoUrl}`;
              } else {
                logoUrl = `${baseUrl}/${logoUrl}`;
              }
            }
            
            setCompanyLogo(logoUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching company logo:', error);
      }
    };

    fetchCompanyLogo();
  }, [logoFetchAttempted]);

  if (!productStatusChange) {
    return <div>No product status change data available</div>;
  }

  // Helper function to format dates
  const formatDate = (date) => {
    if (!date) return '';
    try {
      return dayjs(date).format('DD/MM/YYYY');
    } catch {
      return date;
    }
  };

  const formatDateTime = (date) => {
    if (!date) return '';
    try {
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    } catch {
      return date;
    }
  };

  // Get status checkbox states
  const statusName = productStatusChange.productStatus?.name || '';
  const isQuarantined = statusName.toLowerCase().includes('quarantine') || statusName.toLowerCase() === 'quarantined';
  const isRejected = statusName.toLowerCase().includes('reject') || statusName.toLowerCase() === 'rejected';
  const isReleased = statusName.toLowerCase().includes('release') || statusName.toLowerCase() === 'released';

  return (
    <div className="product-status-change-report print-only" style={styles.container}>
      {/* Print Styles */}
      <style>{printStyles}</style>

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.headerLeft}>
            {companyLogo ? (
              <img 
                src={companyLogo} 
                alt="Company Logo" 
                style={styles.logo}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : null}
          </div>
          <div style={styles.headerRight}>
            <div style={styles.documentId}>EN.05 ΟΔ.01/06-ΓΔ.31</div>
          </div>
        </div>
      </div>

      {/* Main Form Table */}
      <table style={styles.mainTable}>
        <tbody>
          {/* Title Row */}
          <tr>
            <td colSpan="4" style={styles.titleCell}>
              MERCK PRODUCT STATUS CHANGE CONFIRMATION
            </td>
          </tr>

          {/* Product Name Row */}
          <tr>
            <td style={styles.labelCell}>Name of Product:</td>
            <td style={styles.valueCell} colSpan="3">{productStatusChange.productName || ''}</td>
          </tr>

          {/* Item Code and Batch Number Row */}
          <tr>
            <td style={styles.labelCell}>Item Code:</td>
            <td style={styles.valueCell}>{productStatusChange.itemCode || ''}</td>
            <td style={styles.labelCell}>Batch Number:</td>
            <td style={styles.valueCell}>{productStatusChange.batchNumber || ''}</td>
          </tr>

          {/* Expiry Date and Quantity Row */}
          <tr>
            <td style={styles.labelCell}>Expiry Date:</td>
            <td style={styles.valueCell}>{formatDate(productStatusChange.expiryDate)}</td>
            <td style={styles.labelCell}>Quantity (items):</td>
            <td style={styles.valueCell}>{productStatusChange.quantity || ''}</td>
          </tr>

          {/* Merck Reference and Certificate Date Row */}
          <tr>
            <td style={styles.labelCell}>Merck Reference: (ManGo ID)</td>
            <td style={styles.valueCell}>{productStatusChange.merckReference || ''}</td>
            <td style={styles.labelCell}>Merck Batch Disposition Certificate Date:</td>
            <td style={styles.valueCell}>{formatDate(productStatusChange.merckDispositionCertDate)}</td>
          </tr>

          {/* Status Row */}
          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.statusContainer}>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={isQuarantined} readOnly style={styles.checkbox} />
                  Quarantined
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={isRejected} readOnly style={styles.checkbox} />
                  Rejected
                </label>
                <label style={styles.checkboxLabel}>
                  <input type="checkbox" checked={isReleased} readOnly style={styles.checkbox} />
                  Released
                </label>
              </div>
            </td>
          </tr>

          {/* Comments/Remarks Row */}
          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.commentsSection}>
                <div style={styles.commentsLabel}>Document Review and Approval Status:</div>
                <div style={styles.commentsTextArea}>
                  {productStatusChange.remarks || ''}
                </div>
              </div>
            </td>
          </tr>

          {/* QP Signature Section */}
          <tr>
            <td style={styles.labelCell} colSpan="4">
              <div style={styles.qpSection}>
                <div style={styles.qpTitle}>ALFAOMEGA SA QP</div>
                <div style={styles.qpLabel}>Name / Signature / Date:</div>
                <div style={styles.qpSignatureLine}>
                  {productStatusChange.qpUser?.userDisplayName || productStatusChange.qpUser?.name || ''}
                  {productStatusChange.qpUserSignedAt && ` - ${formatDateTime(productStatusChange.qpUserSignedAt)}`}
                </div>
              </div>
            </td>
          </tr>

          {/* Last Approval Status - similar to Complaint print report */}
          {Array.isArray(productStatusChange.approvalRequests) && productStatusChange.approvalRequests.length > 0 && (() => {
            const approvalRequests = productStatusChange.approvalRequests || [];
            const sortedApprovals = [...approvalRequests].sort((a, b) => {
              const dateA = new Date(a.requestedAt || a.createdAt || '1970-01-01');
              const dateB = new Date(b.requestedAt || b.createdAt || '1970-01-01');
              return dateB.getTime() - dateA.getTime();
            });
            const lastApproval = sortedApprovals[0];

            if (!lastApproval) return null;

            return (
              <>
                {/* Spacer row */}
                <tr>
                  <td style={styles.labelCell} colSpan="4">
                    <div style={{ fontWeight: 'bold', marginTop: '15px', marginBottom: '8px' }}>
                      Last Approval Status
                    </div>
                  </td>
                </tr>
                <tr>
                  <td style={styles.labelCell}>Approval Code:</td>
                  <td style={styles.valueCell}>
                    <strong>{lastApproval.code || 'N/A'}</strong>
                  </td>
                  <td style={styles.labelCell}>Status:</td>
                  <td style={styles.valueCell}>
                    <strong>{lastApproval.status?.name || lastApproval.status || 'N/A'}</strong>
                  </td>
                </tr>
                <tr>
                  <td style={styles.labelCell}>Title:</td>
                  <td style={styles.valueCell} colSpan="3">
                    {lastApproval.title || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td style={styles.labelCell}>Requested By:</td>
                  <td style={styles.valueCell}>
                    {lastApproval.requestedBy?.userDisplayName || lastApproval.requestedBy?.name || 'N/A'}
                  </td>
                  <td style={styles.labelCell}>Requested Date:</td>
                  <td style={styles.valueCell}>
                    {formatDateTime(lastApproval.requestedAt)}
                  </td>
                </tr>
                <tr>
                  <td style={styles.labelCell}>Due Date:</td>
                  <td style={styles.valueCell}>
                    {formatDate(lastApproval.dueDate)}
                  </td>
                  <td style={styles.labelCell}>Completed Date:</td>
                  <td style={styles.valueCell}>
                    {formatDateTime(lastApproval.completedAt)}
                  </td>
                </tr>
              </>
            );
          })()}
        </tbody>
      </table>
    </div>
  );
};

// Styles
const styles = {
  container: {
    maxWidth: '210mm',
    margin: '0 auto',
    padding: '5mm',
    backgroundColor: '#fff',
    fontFamily: 'Arial, sans-serif',
    fontSize: '11pt',
    lineHeight: '1.4',
    color: '#000',
  },
  header: {
    borderBottom: '2px solid #000',
    paddingBottom: '10px',
    marginBottom: '20px',
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerLeft: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
  },
  headerRight: {
    flex: '1 1 auto',
    textAlign: 'right',
  },
  logo: {
    maxHeight: '50px',
    maxWidth: '200px',
    height: 'auto',
    width: 'auto',
    objectFit: 'contain',
    marginRight: '20px',
  },
  documentId: {
    fontSize: '11pt',
    color: '#666',
    fontWeight: 'normal',
  },
  mainTable: {
    width: '100%',
    borderCollapse: 'collapse',
    border: '2px solid #000',
    marginBottom: '20px',
  },
  titleCell: {
    padding: '15px',
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '14pt',
    border: '2px solid #000',
    backgroundColor: '#fff',
  },
  labelCell: {
    padding: '10px',
    fontWeight: 'bold',
    backgroundColor: '#fff',
    border: '1px solid #000',
    verticalAlign: 'top',
    width: '25%',
  },
  valueCell: {
    padding: '10px',
    border: '1px solid #000',
    verticalAlign: 'top',
    width: '25%',
  },
  statusContainer: {
    display: 'flex',
    gap: '20px',
    padding: '10px 0',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'default',
    fontSize: '11pt',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    cursor: 'default',
    pointerEvents: 'none',
  },
  commentsSection: {
    padding: '10px 0',
  },
  commentsLabel: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  commentsTextArea: {
    minHeight: '80px',
    padding: '8px',
    border: '1px solid #ccc',
    backgroundColor: '#fff',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  qpSection: {
    padding: '15px 0',
  },
  qpTitle: {
    fontWeight: 'bold',
    fontSize: '12pt',
    marginBottom: '10px',
  },
  qpLabel: {
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  qpSignatureLine: {
    minHeight: '40px',
    borderBottom: '1px solid #000',
    paddingBottom: '5px',
    marginTop: '10px',
  },
};

// Print-specific styles
const printStyles = `
  @media print {
    @page {
      size: A4;
      margin: 5mm;
    }
    
    /* Hide print controls and navigation */
    .no-print {
      display: none !important;
    }
    
    /* Hide any headers, sidebars, navigation from MainLayout */
    header, nav, aside, .sidebar, .main-header, .app-header,
    [class*="header"]:not(.product-status-change-report), [class*="nav"], [class*="sidebar"] {
      display: none !important;
    }
    
    /* Ensure report wrapper is visible */
    .product-status-change-report-wrapper {
      display: block !important;
      visibility: visible !important;
      width: 100% !important;
      height: auto !important;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: white !important;
      height: auto !important;
      min-height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      width: 100% !important;
    }
    
    .product-status-change-report-wrapper {
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      height: auto !important;
      overflow: visible !important;
    }
    
    .product-status-change-report {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 5mm !important;
      box-shadow: none !important;
      height: auto !important;
      overflow: visible !important;
      page-break-after: auto;
      position: relative !important;
      background: white !important;
      display: block !important;
      visibility: visible !important;
    }
    
    .no-print {
      display: none !important;
    }
    
    .print-only {
      display: block !important;
    }
    
    /* Allow sections to break across pages */
    section {
      page-break-inside: auto;
      page-break-after: auto;
      margin-bottom: 15px;
      overflow: visible !important;
    }
    
    /* Allow tables to break across pages */
    table {
      page-break-inside: auto;
      border-collapse: collapse;
      width: 100% !important;
    }
    
    /* Keep table headers on each page */
    thead {
      display: table-header-group;
    }
    
    tbody {
      display: table-row-group;
    }
    
    tr {
      page-break-inside: auto;
      page-break-after: auto;
    }
    
    /* Try to keep section titles with their content */
    h2 {
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    
    /* Allow cells to break if needed */
    td, th {
      page-break-inside: auto;
    }
    
    /* Prevent orphaned content */
    p, div {
      orphans: 3;
      widows: 3;
    }
    
    /* Ensure all content is visible for printing */
    * {
      visibility: visible !important;
    }
  }
  
  @media screen {
    .product-status-change-report {
      box-shadow: 0 0 10px rgba(0,0,0,0.1);
      margin: 20px auto;
    }
  }
`;

export default ProductStatusChangeReport;

